import type { SportEvent } from '../../types/SportEventTypes';
import type { StatDefinition } from '../types';
import { creditField, participantMainId, readPayload } from '../shared';
import { pointsForNbaEvent } from './points';

/**
 * Points are the one weighted NBA stat: a made basket/free throw credits its
 * shooter with 2 / 3 / 1 (per ./points.ts) rather than a flat count.
 */
function attributePoints(event: SportEvent): ReadonlyArray<{ playerId: string; value?: number }> {
  const value = pointsForNbaEvent(event);
  if (value <= 0) return [];
  const playerId = participantMainId(readPayload(event).data?.shooter);
  return playerId ? [{ playerId, value }] : [];
}

/**
 * NBA per-player stats (5 for now). Rebounds / FGM / fouls / turnovers are
 * plain event counts; points are weighted. Assists are intentionally omitted —
 * the SES basketball feed doesn't carry an assister on baskets today.
 */
export const NBA_STATS: ReadonlyArray<StatDefinition> = [
  { key: 'points', label: 'PTS', fullName: 'Points', attribute: attributePoints },
  {
    key: 'rebounds',
    label: 'REB',
    fullName: 'Rebounds',
    attribute: (e) => creditField(e, ['rebound_offensive', 'rebound_defensive'], 'player'),
  },
  {
    key: 'fieldGoalsMade',
    label: 'FGM',
    fullName: 'Field Goals Made',
    attribute: (e) => creditField(e, ['basket_made_2pt', 'basket_made_3pt'], 'shooter'),
  },
  {
    key: 'fouls',
    label: 'Fouls',
    attribute: (e) => creditField(e, ['foul_personal', 'foul_offensive'], 'player'),
  },
  {
    key: 'turnovers',
    label: 'TO',
    fullName: 'Turnovers',
    attribute: (e) => creditField(e, 'turnover', 'player'),
  },
];
